import React, { useState, useEffect, useCallback, useContext } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import TableContainer from "@material-ui/core/TableContainer";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		minWidth: 720,
	},
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	search: {
		flex: 1,
	},
	tableContainer: {
		maxHeight: 480,
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(3),
		color: theme.palette.text.secondary,
	},
	loadingWrapper: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3),
	},
	selectionInfo: {
		flex: 1,
	},
}));

const formatDate = (isoDate) => {
	if (!isoDate) return "-";
	try {
		const d = new Date(isoDate);
		return d.toLocaleString("pt-BR");
	} catch {
		return isoDate;
	}
};

const formatCreatedBy = (entry) => {
	if (entry?.createdByUser?.name) return entry.createdByUser.name;
	if (entry?.createdBy == null) return "Sistema";
	return "Usuário removido";
};

const BlacklistModal = ({ open, onClose }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedIds, setSelectedIds] = useState([]);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const connectionLabel = useCallback(
		(connectionId) => {
			if (connectionId == null) return "Todas as conexões";
			const match = (user?.whatsapps || []).find((w) => w.id === connectionId);
			return match ? match.name : `Conexão #${connectionId}`;
		},
		[user]
	);

	const fetchBlacklist = useCallback(async () => {
		if (!open) return;
		setLoading(true);
		try {
			const { data } = await api.get("/blacklist", {
				params: searchParam ? { searchParam } : {},
			});
			setRows(Array.isArray(data?.blacklist) ? data.blacklist : []);
			setSelectedIds([]);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [open, searchParam]);

	useEffect(() => {
		if (open) fetchBlacklist();
		else {
			setRows([]);
			setSelectedIds([]);
			setSearchParam("");
		}
	}, [open, fetchBlacklist]);

	const handleToggleAll = (e) => {
		if (e.target.checked) {
			setSelectedIds(rows.map((r) => r.id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleToggleOne = (id) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const handleAskRemove = () => {
		if (selectedIds.length === 0) return;
		setConfirmOpen(true);
	};

	const handleConfirmRemove = async () => {
		if (selectedIds.length === 0) return;
		setRemoving(true);
		try {
			await api.post("/blacklist/bulk-delete", { ids: selectedIds });
			// remove localmente para resposta instantânea
			setRows((prev) => prev.filter((r) => !selectedIds.includes(r.id)));
			setSelectedIds([]);
		} catch (err) {
			toastError(err);
		} finally {
			setRemoving(false);
		}
	};

	const allSelected = rows.length > 0 && selectedIds.length === rows.length;
	const someSelected = selectedIds.length > 0 && !allSelected;

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				fullWidth
				maxWidth="md"
				classes={{ paper: classes.dialogPaper }}
			>
				<DialogTitle>Contatos na blacklist</DialogTitle>
				<DialogContent dividers>
					<div className={classes.toolbar}>
						<TextField
							className={classes.search}
							variant="outlined"
							size="small"
							placeholder="Buscar por nome ou número..."
							value={searchParam}
							onChange={(e) => setSearchParam(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") fetchBlacklist();
							}}
						/>
						<Button variant="outlined" onClick={fetchBlacklist}>
							Buscar
						</Button>
					</div>

					{loading ? (
						<div className={classes.loadingWrapper}>
							<CircularProgress size={28} />
						</div>
					) : rows.length === 0 ? (
						<Typography className={classes.emptyState}>
							Nenhum contato na blacklist.
						</Typography>
					) : (
						<TableContainer
							component={Paper}
							variant="outlined"
							className={classes.tableContainer}
						>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												checked={allSelected}
												indeterminate={someSelected}
												onChange={handleToggleAll}
											/>
										</TableCell>
										<TableCell>Nome</TableCell>
										<TableCell>Número</TableCell>
										<TableCell>Conexão</TableCell>
										<TableCell>Adicionado por</TableCell>
										<TableCell>Bloqueado em</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((entry) => {
										const isSelected = selectedIds.includes(entry.id);
										return (
											<TableRow
												key={entry.id}
												hover
												selected={isSelected}
												onClick={() => handleToggleOne(entry.id)}
												style={{ cursor: "pointer" }}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={isSelected}
														onChange={() => handleToggleOne(entry.id)}
														onClick={(e) => e.stopPropagation()}
													/>
												</TableCell>
												<TableCell>{entry.contact?.name || "-"}</TableCell>
												<TableCell>{entry.contact?.number || "-"}</TableCell>
												<TableCell>{connectionLabel(entry.connection)}</TableCell>
												<TableCell>{formatCreatedBy(entry)}</TableCell>
												<TableCell>{formatDate(entry.createdAt)}</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</DialogContent>
				<DialogActions>
					<Typography variant="body2" className={classes.selectionInfo}>
						{selectedIds.length > 0
							? `${selectedIds.length} selecionado(s)`
							: ""}
					</Typography>
					<BotaoCancelar onClick={onClose}>
						Fechar
					</BotaoCancelar>
					<BotaoConfirmar
						disabled={selectedIds.length === 0}
						loading={removing}
						onClick={handleAskRemove}
					>
						Remover da blacklist
					</BotaoConfirmar>
				</DialogActions>
			</Dialog>

			<ConfirmationModal
				title={`Remover ${selectedIds.length} contato(s) da blacklist?`}
				open={confirmOpen}
				onClose={setConfirmOpen}
				onConfirm={handleConfirmRemove}
			>
				Os contatos voltarão a receber e enviar mensagens normalmente e
				suas conversas voltarão a aparecer nas listagens.
			</ConfirmationModal>
		</>
	);
};

export default BlacklistModal;
